import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RSMIN_APP_URL } from '../config';
import { tenantReturnPath } from '../utils/tenantRouting';

export default function RSMinSignInButton({ tenantSlug, search = '' }) {
  const { loginWithRSMinToken, user } = useAuth();
  const navigate = useNavigate();
  const attempt = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const destination = tenantReturnPath(tenantSlug, search);

  useEffect(() => {
    if (connected && user) navigate(destination, { replace: true });
  }, [connected, user, destination, navigate]);

  useEffect(() => () => {
    const active = attempt.current;
    active?.dispose();
    active?.popup.close();
  }, []);

  if (tenantSlug !== 'rsmin' || !RSMIN_APP_URL || (user && !busy && !connected)) return null;

  const start = () => {
    setError('');
    setConnected(false);
    let origin;
    let url;
    let state;
    try {
      const base = new URL(RSMIN_APP_URL);
      if (!['http:', 'https:'].includes(base.protocol)) throw new Error('Invalid RSMIN URL');
      origin = base.origin;
      state = Array.from(window.crypto.getRandomValues(new Uint8Array(32)), (value) => value.toString(16).padStart(2, '0')).join('');
      url = new URL('/auth/redirect-to-cip', base);
      url.search = new URLSearchParams({
        redirectUri: `${window.location.origin}/auth/rsmin-callback`, popup: '1', state,
      }).toString();
    } catch {
      setError('RSMIN sign-in is not configured correctly. Please contact your administrator.');
      return;
    }

    const popup = window.open(url.href, '_blank', 'popup,width=520,height=720');
    if (!popup) {
      setError('Your browser blocked the RSMIN sign-in window. Allow pop-ups for this site and try again.');
      return;
    }
    setBusy(true);
    let processing = false;
    let timer;
    const dispose = () => {
      window.removeEventListener('message', receive);
      window.clearInterval(timer);
      attempt.current = null;
    };
    const fail = (message) => {
      dispose();
      popup.close();
      setBusy(false);
      setError(message);
    };
    const receive = async (event) => {
      if (event.origin !== origin || event.source !== popup || event.data?.type !== 'rsmin:signin'
        || event.data.state !== state || typeof event.data.token !== 'string' || !event.data.token || processing) return;
      processing = true;
      window.clearInterval(timer);
      try {
        await loginWithRSMinToken(event.data.token);
        if (attempt.current?.popup !== popup) return;
        dispose();
        popup.close();
        setConnected(true);
        setBusy(false);
      } catch (err) {
        if (attempt.current?.popup !== popup) return;
        fail(err.response?.data?.message || 'Unable to verify your RSMIN sign-in. Please try again.');
      }
    };
    window.addEventListener('message', receive);
    const started = Date.now();
    timer = window.setInterval(() => {
      if (popup.closed) fail('The RSMIN sign-in window was closed before connecting. Please try again.');
      else if (Date.now() - started > 5 * 60 * 1000) fail('RSMIN sign-in timed out. Please try again.');
    }, 500);
    attempt.current = { popup, dispose };
  };

  return <div className="rsmin-signin">
    <button type="button" onClick={start} disabled={busy}>
      {busy ? 'Connecting to RSMIN...' : 'Sign in with RSMIN'}
    </button>
    <p>Use your RSMIN account in a separate sign-in window.</p>
    {busy && <p role="status">Complete sign-in in the RSMIN window to return here.</p>}
    {error && <p className="error-msg" role="alert">{error}</p>}
  </div>;
}
